import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { useRobots } from "@/components/seo/Seo";
import { Check, ChevronDown, Globe, Loader2, MapPin, Navigation, Phone, Satellite } from "lucide-react";
import Logo from "@/components/Logo";
import { DRIVER_I18N, DRIVER_LANGS, DRIVER_LANG_NAMES, LOCALES } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const dlResolve = (lang, key) =>
  key.split(".").reduce((o, k) => (o != null ? o[k] : undefined), DRIVER_I18N[lang])
  ?? key.split(".").reduce((o, k) => (o != null ? o[k] : undefined), DRIVER_I18N.fr)
  ?? key;

export default function DriverLink() {
  const { token } = useParams();
  useRobots("noindex, nofollow");
  // langue du chauffeur : choix persisté, sinon détection du téléphone (FR par défaut)
  const [lang, setLangState] = useState(() => {
    try {
      const saved = localStorage.getItem("miles-driver-lang");
      if (saved && DRIVER_LANGS.includes(saved)) return saved;
    } catch { /* noop */ }
    const nav = (navigator.language || "fr").slice(0, 2).toLowerCase();
    return DRIVER_LANGS.includes(nav) ? nav : "fr";
  });
  const setLang = (l) => {
    if (!DRIVER_LANGS.includes(l)) return;
    setLangState(l);
    try { localStorage.setItem("miles-driver-lang", l); } catch { /* noop */ }
  };
  const t = (key) => dlResolve(lang, key);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const langMenuRef = useRef(null);
  useEffect(() => {
    if (!langMenuOpen) return undefined;
    const close = (e) => { if (langMenuRef.current && !langMenuRef.current.contains(e.target)) setLangMenuOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [langMenuOpen]);
  const [trip, setTrip] = useState(null);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [shareError, setShareError] = useState("");
  const watchRef = useRef(null);
  const heartbeatRef = useRef(null);
  const wakeLockRef = useRef(null);
  const lastSentRef = useRef(0);
  const [lastSentAt, setLastSentAt] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/driver/${token}`).then((r) => setTrip(r.data)).catch(() => setError(true));
  }, [token]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    document.title = "MILES Chauffeur";
    let robots = document.querySelector('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement("meta");
      robots.setAttribute("name", "robots");
      document.head.appendChild(robots);
    }
    robots.setAttribute("content", "noindex, nofollow");
  }, []);

  const advance = async (status) => {
    if (busy) return;
    setBusy(true);
    try {
      await axios.post(`${API}/driver/${token}/status`, { status, origin_url: window.location.origin });
      load();
    } catch { /* transition refusée */ } finally { setBusy(false); }
  };

  // envoi de position (partagé par watchPosition, le battement de cœur et le retour au premier plan)
  const sendPosition = useCallback((pos, force = false) => {
    const now = Date.now();
    if (!force && now - lastSentRef.current < 3000) return; // envoi max toutes les 3 s
    lastSentRef.current = now;
    setLastSentAt(now);
    axios.post(`${API}/driver/${token}/location`, {
      lat: pos.coords.latitude, lon: pos.coords.longitude, sharing: true,
    }).catch(() => {});
  }, [token]);

  // garder l'écran allumé pendant la course (iOS 16.4+) — sinon la veille endort le GPS
  const keepAwake = useCallback(() => {
    if (!("wakeLock" in navigator)) return;
    navigator.wakeLock.request("screen").then((lock) => { wakeLockRef.current = lock; }).catch(() => {});
  }, []);

  // Partage de position en direct : le client voit l'avancée sur la carte du suivi.
  const stopSharing = useCallback(async (notify = true) => {
    if (watchRef.current !== null) {
      navigator.geolocation.clearWatch(watchRef.current);
      watchRef.current = null;
    }
    if (heartbeatRef.current !== null) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
    if (wakeLockRef.current) {
      wakeLockRef.current.release().catch(() => {});
      wakeLockRef.current = null;
    }
    setSharing(false);
    if (notify) {
      try { await axios.post(`${API}/driver/${token}/location`, { lat: 0, lon: 0, sharing: false }); } catch { /* silencieux */ }
    }
  }, [token]);

  const startSharing = useCallback(() => {
    setShareError("");
    if (!("geolocation" in navigator)) {
      setShareError("err_geo");
      return;
    }
    setSharing(true);
    lastSentRef.current = 0;
    watchRef.current = navigator.geolocation.watchPosition(
      (pos) => sendPosition(pos),
      () => {
        setShareError("err_denied");
        setSharing(false);
      },
      { enableHighAccuracy: true, maximumAge: 5000 },
    );
    // battement de cœur : relance une lecture GPS même si watchPosition se tait (veille iOS)
    heartbeatRef.current = setInterval(() => {
      navigator.geolocation.getCurrentPosition((pos) => sendPosition(pos), () => {},
        { enableHighAccuracy: true, maximumAge: 1500, timeout: 4000 });
    }, 3000);
    keepAwake(); // écran allumé pendant la course : sinon la veille endort le GPS
  }, [token, lang, sendPosition, keepAwake]);

  // retour au premier plan (déverrouillage) : position immédiate + écran ré-armé
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== "visible" || !sharing) return;
      keepAwake();
      navigator.geolocation?.getCurrentPosition((pos) => sendPosition(pos, true), () => {},
        { enableHighAccuracy: true, timeout: 8000 });
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [sharing, sendPosition, keepAwake]);

  // chien de garde : partage actif mais aucun envoi réussi depuis 60 s → alarme visible.
  // (En arrière-plan iOS suspend la page ; au retour, l'envoi forcé éteint l'alarme.)
  const [sendStalled, setSendStalled] = useState(false);
  useEffect(() => {
    if (!sharing) { setSendStalled(false); return undefined; }
    const id = setInterval(() => {
      setSendStalled(lastSentRef.current > 0 && Date.now() - lastSentRef.current > 60000);
    }, 5000);
    return () => clearInterval(id);
  }, [sharing]);

  // Une fois activé, le partage ne se coupe JAMAIS tout seul : seul le chauffeur l'éteint.
  // Quitter la page arrête juste la montre locale (le flag serveur reste actif →
  // reprise automatique à la réouverture du lien).
  useEffect(() => () => stopSharing(false), [stopSharing]);

  // reprise automatique : si le partage est actif côté serveur, on le relance au chargement
  const autoStartedRef = useRef(false);
  useEffect(() => {
    if (trip?.sharing && !sharing && !autoStartedRef.current) {
      autoStartedRef.current = true;
      startSharing();
    }
  }, [trip, sharing, startSharing]);

  if (error) return (
    <div className="flex min-h-screen items-center justify-center bg-cloud px-5" data-testid="driver-link-invalid">
      <p className="text-sm text-slate">{t("invalid")}</p>
    </div>
  );
  if (!trip) return (
    <div className="flex min-h-screen items-center justify-center bg-cloud">
      <Loader2 className="h-6 w-6 animate-spin text-miles" strokeWidth={1.5} />
    </div>
  );

  const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(trip.status === "on_board" ? (trip.dropoff || trip.pickup) : trip.pickup)}`;

  return (
    <div className="min-h-screen bg-cloud pb-32" data-testid="driver-link-page">
      <header className="border-b border-slate/10 bg-white px-5 py-4">
        <div className="mx-auto flex max-w-lg items-center justify-between">
          <Logo variant="blue" className="h-5" testId="driver-logo" />
          <div className="flex items-center gap-2.5">
            <span className="font-mono text-xs text-slate">{trip.tracking_code}</span>
            <div className="relative" ref={langMenuRef}>
              <button type="button" onClick={() => setLangMenuOpen((o) => !o)} data-testid="driver-lang-switch"
                aria-expanded={langMenuOpen}
                className="inline-flex items-center gap-1.5 rounded-full border border-slate/20 bg-white px-3 py-1.5 text-[11px] font-semibold text-ink transition-colors hover:border-miles hover:text-miles">
                <Globe className="h-3.5 w-3.5" strokeWidth={1.5} />
                {lang.toUpperCase()}
                <ChevronDown className={`h-3 w-3 transition-transform duration-300 ${langMenuOpen ? "rotate-180" : ""}`} strokeWidth={2} />
              </button>
              {langMenuOpen && (
                <div className="absolute right-0 top-full z-30 mt-2 w-40 overflow-hidden rounded-2xl border border-ink/10 bg-white p-1.5 shadow-xl" data-testid="driver-lang-menu">
                  {DRIVER_LANGS.map((l) => (
                    <button key={l} data-testid={`driver-lang-${l}`}
                      onClick={() => { setLang(l); setLangMenuOpen(false); }}
                      className={`flex w-full items-center justify-between rounded-xl px-3 py-2 text-xs font-semibold transition-colors duration-200 ${lang === l ? "bg-miles-ice text-miles" : "text-ink hover:bg-cloud"}`}>
                      {DRIVER_LANG_NAMES[l]}
                      {lang === l && <Check className="h-3.5 w-3.5" strokeWidth={2.5} />}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-lg space-y-4 px-5 py-6">
        {sendStalled && (
          <div className="animate-pulse rounded-md border border-red-300 bg-red-50 px-4 py-3" data-testid="driver-stalled-banner">
            <p className="text-sm font-semibold text-red-700">{t("stalled")}</p>
          </div>
        )}
        <div className="rounded-md border border-miles-sky bg-miles-ice px-4 py-3" data-testid="driver-status">
          <p className="text-sm font-semibold text-miles">{t(`status.${trip.status}`)}</p>
        </div>

        <section className="rounded-md border border-slate/15 bg-white p-4" data-testid="driver-trip">
          <p className="font-mono text-2xl font-semibold text-ink">{trip.time}</p>
          <p className="text-sm text-slate">{trip.date}</p>
          <div className="mt-4 space-y-3">
            <div className="flex gap-3">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-miles" strokeWidth={1.5} />
              <div>
                <p className="text-xs text-slate">{t("pickup")}</p>
                <p className="text-sm font-medium text-ink" data-testid="driver-pickup">{trip.pickup}</p>
              </div>
            </div>
            <div className="flex gap-3">
              <Navigation className="mt-0.5 h-4 w-4 shrink-0 text-miles-deep" strokeWidth={1.5} />
              <div>
                <p className="text-xs text-slate">{t("dest")}</p>
                <p className="text-sm font-medium text-ink">{trip.dropoff || t("hourly").replace("{h}", trip.duration_hours || 2)}</p>
              </div>
            </div>
          </div>
          <a href={mapsUrl} target="_blank" rel="noreferrer" data-testid="driver-nav"
            className="mt-4 flex h-11 items-center justify-center gap-2 rounded border border-miles/30 bg-white text-sm font-semibold text-miles">
            <Navigation className="h-4 w-4" strokeWidth={1.5} /> {t("nav")}
          </a>
        </section>

        <section className="rounded-md border border-slate/15 bg-white p-4" data-testid="driver-location-sharing">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-start gap-3">
              <Satellite className={`mt-0.5 h-4 w-4 shrink-0 ${sharing ? "text-emerald-600" : "text-slate"}`} strokeWidth={1.5} />
              <div>
                <p className="text-sm font-semibold text-ink">{t("share_t")}</p>
                <p className="text-xs text-slate">
                  {sharing ? t("share_on") : t("share_off")}
                </p>
                {sharing && lastSentAt && (
                  <p className="mt-1 flex items-center gap-1.5 text-[11px] font-medium text-emerald-600" data-testid="driver-last-sent">
                    <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" /><span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" /></span>
                    {t("sent").replace("{time}", new Date(lastSentAt).toLocaleTimeString(LOCALES[lang], { hour: "2-digit", minute: "2-digit", second: "2-digit" }))}
                  </p>
                )}
                {shareError && <p className="mt-1 text-xs font-medium text-red-600">{t(shareError)}</p>}
              </div>
            </div>
            <button type="button" role="switch" aria-checked={sharing}
              onClick={() => (sharing ? stopSharing() : startSharing())}
              data-testid="driver-share-location"
              className={`relative h-7 w-12 shrink-0 rounded-full transition-colors ${sharing ? "bg-emerald-500" : "bg-ink/15"}`}>
              <span className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition-all ${sharing ? "left-[22px]" : "left-0.5"}`} />
            </button>
          </div>
        </section>

        <section className="rounded-md border border-slate/15 bg-white p-4" data-testid="driver-passenger">          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-slate">{t("passenger")}</p>
              <p className="text-sm font-semibold text-ink">{trip.passenger?.first_name} {trip.passenger?.last_name}</p>
              <p className="text-xs text-slate">{t("pax").replace("{p}", trip.passengers).replace("{l}", trip.luggage)}</p>
            </div>
            {trip.passenger?.phone && (
              <a href={`tel:${trip.passenger.phone}`} data-testid="driver-call"
                className="flex h-11 w-11 items-center justify-center rounded-full bg-miles text-white">
                <Phone className="h-5 w-5" strokeWidth={1.5} />
              </a>
            )}
          </div>
          {trip.flight_number && <p className="mt-3 font-mono text-xs text-slate">{t("flight").replace("{f}", trip.flight_number)}{trip.terminal ? ` · T${trip.terminal}` : ""}</p>}
          {trip.notes && <p className="mt-2 rounded bg-cloud px-3 py-2 text-sm text-ink">{trip.notes}</p>}
          {trip.vehicle?.plate && (
            <p className="mt-3 font-mono text-xs text-slate">{trip.vehicle.vehicle_model} · {trip.vehicle.vehicle_color} · {trip.vehicle.plate}</p>
          )}
        </section>
      </main>

      {trip.allowed_next.length > 0 && (
        <div className="fixed inset-x-0 bottom-0 border-t border-slate/10 bg-white/95 p-4 backdrop-blur">
          <div className="mx-auto max-w-lg">
            <button type="button" disabled={busy} onClick={() => advance(trip.allowed_next[0])} data-testid="driver-next-status"
              className="flex h-13 w-full items-center justify-center gap-2 rounded bg-miles py-3.5 text-base font-semibold text-white transition-colors hover:bg-[#1c45cc] disabled:opacity-50">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Check className="h-5 w-5" strokeWidth={2} />}
              {t(`next.${trip.allowed_next[0]}`)}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
